import React from "react";
import { useFormik } from "formik";
import PropTypes from "prop-types";
import * as Yup from "yup";
import { KeyboardDatePicker } from "@material-ui/pickers";

import {
  converToUtc,
  getFormattedDateTimeV2
} from "../../utils/dateTimeHelper";

import Button from "../../components/button";
import ICInput from "../../components/form/input";
import ICCheckbox from "../../components/form/checkbox";
import RefreshButton from "../../components/refreshButton";
import TextField from "@material-ui/core/TextField";
import { Switch } from '@material-ui/core';
import { COUPON_TYPES } from "../../constants/helperConstants";

const CouponForm = props => {
  const formik = useFormik({
    enableReinitialize: true,
    initialValues:
      props.data !== null
        ? {
          code: props.data.code,
          expiryDate: getFormattedDateTimeV2(props.data.expiryDate),
          oneTimeUseCheckbox: props.data.oneTimeUseBool,
          isPercent: props.data.isPercent ? "Percent" : "Amount",
          isActive: props.data.isActive,
          amount: props.data.amount,
          couponTypes: props.data.couponTypes,
          instantConsultCheckbox: props.data.couponTypes.includes("INSTANT_CONSULT"),
          instantSwitchCheckbox: props.data.couponTypes.includes("INSTANT_SWITCH"),
          instantLeafCheckbox: props.data.couponTypes.includes("INSTANT_LEAF"),
          instantHushCheckbox: props.data.couponTypes.includes("INSTANT_HUSH")
        }
        : {
          code: "",
          expiryDate: null,
          oneTimeUseCheckbox: false,
          isPercent: "Amount",
          isActive: true,
          amount: 0,
          instantConsultCheckbox: false,
          instantSwitchCheckbox: false,
          instantLeafCheckbox: false,
          instantHushCheckbox: false
        },
    validationSchema: Yup.object({
      expiryDate: Yup.mixed().required("Please select expiry date"),
      code: Yup.string().required("Please enter Coupon"),
      amount: Yup.number()
        .required("Please enter Amount")
        .when("isPercent", {
          is: "Percent",
          then: schema => schema.min(0, "Please enter a positive number").max(100, "Please enter a number less than 100"),
          otherwise: schema => schema.min(0)
        }),
        couponTypesCheckbox: Yup.boolean().test(
          'at-least-one',
          'At least one applicable option must be selected',
          function () {
            const { instantConsultCheckbox, instantSwitchCheckbox, instantLeafCheckbox, instantHushCheckbox } = this.parent;
            return instantConsultCheckbox || instantSwitchCheckbox || instantLeafCheckbox || instantHushCheckbox;
          }
        )
    }),
    onSubmit: values => {
      const couponTypes = [];
      
      if (values.instantConsultCheckbox) couponTypes.push(COUPON_TYPES.INSTANT_CONSULT);
      if (values.instantSwitchCheckbox) couponTypes.push(COUPON_TYPES.INSTANT_SWITCH);
      if (values.instantLeafCheckbox) couponTypes.push(COUPON_TYPES.INSTANT_LEAF);
      if (values.instantHushCheckbox) couponTypes.push(COUPON_TYPES.INSTANT_HUSH);
    
      let formData = new FormData();
      formData.append("couponText", values.code.trim());
      formData.append("expiryDate", converToUtc(values.expiryDate));
      formData.append("oneTimeUse", values.oneTimeUseCheckbox);
      formData.append("isPercent", values.isPercent === "Percent");
      formData.append("isActive", values.isActive);
      formData.append("amount", values.amount);
    
      couponTypes.forEach(type => formData.append("couponTypes[]", type));
    
      if (props.isEditMode) {
        formData.append("id", props.data.id);
        props.onEditCoupon(formData);
      } else {
        props.onAddSubmit(formData);
      }
    
      formik.resetForm();
    }
  });

  const generateRandomString = () => {
    let chars = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXTZ";
    /*Length of the Random String you want to Generate*/
    let string_length = 10;
    let randomstring = "";

    for (let i = 0; i < string_length; i++) {
      let rnum = Math.floor(Math.random() * chars.length);
      randomstring += chars.substring(rnum, rnum + 1);
    }

    formik.setFieldValue("code", randomstring);
  };

  return (
    <form
      className="container d-flex flex-column mr-4 pr-4"
      onSubmit={formik.handleSubmit}
    >
      <div className="form-group row">
        <label className="col-sm-4 col-form-label font-demi-bold">
          Coupon:<span className="error">*</span>{" "}
          {!props.isEditMode && (
            <RefreshButton onClick={generateRandomString} />
          )}
        </label>
        <div className="col-sm-8">
          <ICInput
            name="code"
            type="text"
            value={formik.values.code}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={props.isEditMode}
            error={formik.touched.code && formik.errors.code ? true : false}
            helperText={
              formik.touched.code && formik.errors.code
                ? formik.errors.code
                : ""
            }
          />
        </div>
      </div>
      <div className="form-group row">
        <label className="col-sm-4 col-form-label font-demi-bold">
          Expiry Date:<span className="error">*</span>
        </label>
        <div className="col-sm-8">
          <KeyboardDatePicker
            className="w-100"
            disablePast={true}
            name="expiryDate"
            autoOk
            variant="inline"
            mask="__-__-____"
            format="DD-MM-YYYY"
            value={formik.values.expiryDate}
            onChange={date => {
              formik.setFieldValue("expiryDate", date);
            }}
            error={
              formik.touched.expiryDate && formik.errors.expiryDate
                ? true
                : false
            }
            helperText={
              formik.touched.expiryDate && formik.errors.expiryDate
                ? formik.errors.expiryDate
                : ""
            }
          />
        </div>
      </div>
      <div className="form-group row">
        <label className="col-sm-4 col-form-label font-demi-bold">Active</label>
        <div className="col-sm-8">
          <Switch
            //style={{ padding: 0 }}
            className="custom-switch"
            name="isActive"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.isActive}
            checked={formik.values.isActive}
            inputProps={{ "aria-label": "primary checkbox" }}
          />
        </div>
      </div>

      <div className="form-group row">
        <label className="col-sm-4 col-form-label font-demi-bold">Type <span className="error">*</span>{" "}</label>
        <div className="col-sm-8">
          <TextField
            className="w-100"
            select
            name="isPercent"
            SelectProps={{
              native: true
            }}
            value={formik.values.isPercent}
            onChange={e => {
              console.log("set val", e.target.value);
              formik.setFieldValue("isPercent", e.target.value);
            }}
          >
            {["Percent", "Amount"].map(val => (
              <option key={val} value={val}>
                {val}
              </option>
            ))}
          </TextField>
        </div>
      </div>
      <div className="form-group row">
        <label className="col-sm-4 col-form-label font-demi-bold">Amount <span className="error">*</span>{" "}</label>
        <div className="col-sm-8">
          <ICInput
            name="amount"
            type="number"
            value={formik.values.amount}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.amount && formik.errors.amount ? true : false}
            helperText={
              formik.touched.amount && formik.errors.amount
                ? formik.errors.amount
                : ""
            }
          />
        </div>
      </div>

      <div className="form-group row">
        <label className="col-sm-4 col-form-label font-demi-bold">
          One Time Use:
        </label>
        <div className="col-sm-8">
          <ICCheckbox
            //style={{ padding: 0 }}
            name="oneTimeUseCheckbox"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.oneTimeUseCheckbox}
            checked={formik.values.oneTimeUseCheckbox}
            inputProps={{ "aria-label": "primary checkbox" }}
          />
        </div>
      </div>
      <div className="form-group row">
        <label className="col-sm-4 col-form-label font-demi-bold">Applicable  <span className="error">*</span>{" "}</label>
        <div className="col-sm-8">
          <ICCheckbox
            label="Instant Consult"
            // style={{ padding: 0 }}
            name="instantConsultCheckbox"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.instantConsultCheckbox}
            checked={formik.values.instantConsultCheckbox}
            inputProps={{ "aria-label": "primary checkbox" }}
          />
          <ICCheckbox
            label="Instant Switch"
            style={{ padding: 0 }}
            name="instantSwitchCheckbox"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.instantSwitchCheckbox}
            checked={formik.values.instantSwitchCheckbox}
            inputProps={{ "aria-label": "primary checkbox" }}
          />
          <ICCheckbox
            label="Instant Leaf"
            style={{ padding: 0 }}
            name="instantLeafCheckbox"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.instantLeafCheckbox}
            checked={formik.values.instantLeafCheckbox}
            inputProps={{ "aria-label": "primary checkbox" }}
          />
          <ICCheckbox
            label="Instant Hush"
            style={{ padding: 0 }}
            name="instantHushCheckbox"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.instantHushCheckbox}
            checked={formik.values.instantHushCheckbox}
            inputProps={{ "aria-label": "primary checkbox" }}
          />
          <br />
        </div>
      </div>
      <br />
      <p className="mb-0 d-flex justify-content-center">
        <Button title={props.isEditMode ? "Update" : "Save"} type="submit" />
        <Button
          className="insta-button-primary ml-3 mr-3 mb-4"
          title="Cancel"
          type="button"
          clickEvent={() => {
            formik.resetForm();
            props.cancelEvent();
          }}
        ></Button>
      </p>
    </form>
  );
};

export default CouponForm;

CouponForm.defaultProps = {
  onAddSubmit: () => { },
  cancelEvent: () => { },
  onEditCoupon: () => { },
  isEditMode: false,
  data: null
};

CouponForm.propTypes = {
  onAddSubmit: PropTypes.func,
  cancelEvent: PropTypes.func,
  onEditCoupon: PropTypes.func,
  isEditMode: PropTypes.bool,
  data: PropTypes.object
};
